import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminTables() {
    const [tables, setTables] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => api.get("/admin/tables").then((r) => setTables(r.data));

    useEffect(() => { load(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare questo tavolo?")) return;
        try {
            await api.delete(`/admin/tables/${id}`);
            toast.success("Tavolo eliminato");
            load();
        } catch { toast.error("Errore"); }
    };

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-tables-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="flex items-start justify-between mb-8">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Tavoli</h1>
                        <p className="text-gray-400 text-sm mt-2">Configura i tavoli del ristorante. Numero, posti e posizione.</p>
                    </div>
                    <button
                        onClick={() => setCreating(true)}
                        className="flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors"
                        data-testid="add-table-btn"
                    >
                        <Plus size={16} /> Nuovo tavolo
                    </button>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {tables.map((t) => (
                        <div key={t.table_id} className="bg-ink-surface border border-white/10 p-5 rounded-sm hover:border-gold/40 transition-colors">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs text-gray-500 tracking-widest uppercase">Tavolo</div>
                                    <div className="font-serif text-4xl text-gold">{t.number}</div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setEditing(t)} className="p-2 text-gray-400 hover:text-gold" data-testid={`edit-table-${t.number}`}>
                                        <Edit3 size={14} strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => handleDelete(t.table_id)} className="p-2 text-gray-400 hover:text-red-400" data-testid={`delete-table-${t.number}`}>
                                        <Trash2 size={14} strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-4 text-sm text-gray-300">
                                <div>{t.seats} posti</div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">
                                    {t.location === "outdoor" ? "Esterno" : "Interno"}
                                </div>
                                {t.notes && <div className="text-xs text-gray-500 mt-2 italic">"{t.notes}"</div>}
                            </div>
                        </div>
                    ))}
                </div>

                {(editing || creating) && (
                    <TableDialog
                        table={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { load(); setEditing(null); setCreating(false); }}
                    />
                )}
            </main>
        </div>
    );
}

function TableDialog({ table, onClose, onSaved }) {
    const [form, setForm] = useState({
        number: table?.number || "",
        seats: table?.seats || 2,
        location: table?.location || "indoor",
        notes: table?.notes || "",
    });

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, number: parseInt(form.number, 10), seats: parseInt(form.seats, 10) };
            if (table) {
                await api.put(`/admin/tables/${table.table_id}`, payload);
                toast.success("Tavolo aggiornato");
            } else {
                await api.post("/admin/tables", payload);
                toast.success("Tavolo creato");
            }
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-ink-surface border border-white/10 rounded-sm p-8 max-w-md w-full space-y-4"
                data-testid="table-dialog"
            >
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-2xl text-white">{table ? "Modifica Tavolo" : "Nuovo Tavolo"}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>

                <Field label="Numero">
                    <input type="number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
                        required min="1" data-testid="table-number-input"
                        className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                </Field>
                <Field label="Posti">
                    <input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })}
                        required min="1" data-testid="table-seats-input"
                        className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                </Field>
                <Field label="Posizione">
                    <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                        data-testid="table-location-input"
                        className="w-full px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                        <option value="indoor">Interno</option>
                        <option value="outdoor">Esterno</option>
                    </select>
                </Field>
                <Field label="Note">
                    <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid="table-notes-input"
                        className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                </Field>

                <button type="submit" className="w-full bg-gold text-black font-semibold py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm" data-testid="table-save-btn">
                    Salva
                </button>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
